import { Component, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote-paged/autocomplete-remote-paged.component';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { standardCommand } from '../../../core/model/command';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { LettingShowing } from '../../../letting/model/letting-showing';
import { DhMarketOptions } from '../../../letting/model/platform-options';
import { LettingAdvertismentOrdinaryService } from '../../../letting/service/letting-advertisment-ordinary.service';
import { LettingAdvertismentService } from '../../../letting/service/letting-advertisment.service';
import { ResidentialProperty } from '../../../overview/model/residential-property';
import { buildingCommand } from '../../command/building.command';
import { Area } from '../../model/area';
import { Building } from '../../model/building';
import { BuildingSpace } from '../../model/building-space';
import { AreaType, AreaTypeToString, CustomPropertyDataType, RentalObjectCategory } from '../../model/const/enums';
import { Entrance } from '../../model/entrance';
import { LinkedCustomProperty } from '../../model/linked-custom-property';
import { Property } from '../../model/property';
import { BuildingFormCommandHandlerResolver } from '../../service/command/building-form-command-handler-resolver.service';
import { BuildingSpaceService } from '../../service/data/building-space.service';
import { BuildingService } from '../../service/data/building.service';
import { DwellingService } from '../../service/data/dwelling.service';
import { PremisesService } from '../../service/data/premises.service';
import { PropertyService } from '../../service/data/property.service';
import { BuildingHelpTextService } from '../../service/helpText/building-help-text.service';
import { BuildingDocumentsComponent } from '../building-documents/building-documents.component';
import { CreateBuildingSpaceDialogComponent } from '../create-building-space-dialog/create-building-space-dialog.component';
import { EditBuildingSpaceDialogComponent } from '../edit-building-space-dialog/edit-building-space-dialog.component';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { DimensionLabels } from '../../../accounting/model/dimension-labels';
import { LanguageService } from '../../../core/service/language.service';

@Component({
    selector: 'app-building',
    templateUrl: './building-form.component.html',
    providers: [
        BuildingHelpTextService,
        BuildingFormCommandHandlerResolver,
        DeleteFormCommandHandler,
        NavigationService,
        LettingAdvertismentService,
        LettingAdvertismentOrdinaryService,
        { provide: DataService, useExisting: BuildingService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BuildingFormComponent extends FormComponentBase<Building> {
  constructor(
    private _fcf: FormControlExFactory,
    private fb: FormBuilder,
    private _helpTextService: BuildingHelpTextService,
    private propertyService: PropertyService,
    private _service: BuildingService,
    private _buildingSpaceService: BuildingSpaceService,
    private _dialogService: ActionWindowService,
    private _injector: Injector,
    private dwellingService: DwellingService,
    private premisesService: PremisesService,
    private readonly userAccountService: UserAccountService,
    private readonly lettingAdvertismentService: LettingAdvertismentService,
    protected readonly lettingAdvertismentOrdinaryService: LettingAdvertismentOrdinaryService,
    private readonly _dimensionLabelsService: DimensionLabelsService,
    private readonly _languageService: LanguageService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: BuildingFormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, _service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
    this.addMenuItemDefinition('BUILDING_FORM.COMMAND.CHANGE_PROPERTY', buildingCommand.moveToProperty);
  }

  properties: any[];
  customProperties: {
    form: FormGroup;
    selection: SelectionItem[];
    showInt: boolean;
    showText: boolean;
    showDate: boolean;
    showBoolean: boolean;
    showSelection: boolean;
    showNumeric: boolean;
  }[] = [];
  @ViewChild(BuildingDocumentsComponent) documentComponent: BuildingDocumentsComponent;

  get entrances(): FormArray {
    return this.form.controls['entrances'] as FormArray;
  }

  get areas(): FormArray {
    return this.form.controls['areas'] as FormArray;
  }

  buildingSpaces: FormArray;

  yearMask: RegExp = /^\d{1,4}/;

  private _createEntranceGroup(model: Entrance): FormGroup {
    return this.fb.group({
      id: this._fcf.createHidden(model.id),
      code: this._fcf.createMandatory('ENTRANCE.CODE', model.code, 'ENTRANCE.HELP_TEXT.CODE'),
      name: this._fcf.createMandatory('ENTRANCE.ENTRANCE', model.name, 'ENTRANCE.HELP_TEXT.NAME'),
    });
  }

  private _createBuildingSpaceGroup(model: BuildingSpace): FormGroup {
    return this.fb.group({
      id: this._fcf.createHidden(model.id),
      caption: this._fcf.createReadonly('BUILDING_SPACE.CAPTION', model.caption),
      spaceNumber: this._fcf.createReadonly('BUILDING_SPACE.SPACE_NUMBER', model.spaceNumber),
      area: this._fcf.createReadonly('BUILDING_SPACE.AREA', model.area),
    });
  }

  private _createAreaGroup(model: Area): FormGroup {
    return this.fb.group({
      id: this._fcf.createHidden(model.id),
      type: this._fcf.createMandatory('AREA.AREA_TYPE', model.type),
      value: this._fcf.createMandatory('AREA.VALUE', model.value),
      measuredDate: this._fcf.create('AREA.MEASURED_DATE', model.measuredDate),
      measuredBy: this._fcf.create('AREA.MEASURED_BY', model.measuredBy),
    });
  }

  protected createModel(): Building {
    return new Building(null);
  }

  protected init(data: Building) {
    this.AddSubscription(this._dimensionLabelsService.getDefaultLabels().subscribe(labels => this.applyDimensionLabels(labels)));

    super.init(data);

    if (data.property) {
      this.AddSubscription(
        this.propertyService
          .get(data.property)
          .pipe(switchMap(property => this._dimensionLabelsService.getForCompany(property?.company)))
          .subscribe(labels => this.applyDimensionLabels(labels))
      );
    }

    this.AddSubscription(
      this.propertyService.getList().subscribe(data => {
        this.properties = data.map((item: Property) => {
          return { name: item.toString(), value: item.id };
        });
        if (this.properties.length === 1) {
          this.form.controls['property'].setValue(this.properties[0].value);
        }
      })
    );

    this.AddSubscription(
      this.form.controls['property'].valueChanges.subscribe(value => {
        if (this.isNew && value) {
          this._service.getNewDefaults(value).subscribe(
            defaults => this.form.controls['number'].setValue(defaults.number),
            error => this.messageService.showError(error)
          );
        }
      })
    );

    this.AddSubscription(
      this.form.controls['property'].valueChanges
        .pipe(switchMap(propertyId => (propertyId ? this.propertyService.get(propertyId) : of(null))))
        .subscribe(property => {
          this._dimensionLabelsService.getForCompany(property?.company).subscribe(labels => this.applyDimensionLabels(labels));
        })
    );

    this.AddSubscription(this._service.getDhMarketOptions().subscribe(res => (this.marketAreas = res.marketAreas)));

    this.loadPropertyStatistics();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.bsSub) this.bsSub.unsubscribe();
  }

  marketAreas: SelectionItem[];
  selectedOrdinary: LettingShowing;

  areaTypes: SelectionItem[] = [
    new SelectionItem(AreaTypeToString(AreaType.BOA), AreaType.BOA),
    new SelectionItem(AreaTypeToString(AreaType.LOA), AreaType.LOA),
    new SelectionItem(AreaTypeToString(AreaType.BRA), AreaType.BRA),
    new SelectionItem(AreaTypeToString(AreaType.BIA), AreaType.BIA),
    new SelectionItem(AreaTypeToString(AreaType.BTA), AreaType.BTA),
    new SelectionItem(AreaTypeToString(AreaType.OVA), AreaType.OVA),
    new SelectionItem(AreaTypeToString(AreaType.AGAR), AreaType.AGAR),
    new SelectionItem(AreaTypeToString(AreaType.ATEMP), AreaType.ATEMP),
    new SelectionItem(AreaTypeToString(AreaType.NTA), AreaType.NTA),
    new SelectionItem(AreaTypeToString(AreaType.LA), AreaType.LA),
  ];

  protected getFormGroup(): FormGroup {
    if (this.dataContext.isNew && this.dataContext.entrances.length === 0) {
      let entrance = new Entrance({ id: this.newGuid(), name: '1' });
      this.dataContext.entrances.push(entrance);
    } else {
      this.dataContext.entrances.sort();
    }

    let group = this.fb.group({
      address: this.fb.group({}),
      buildYear: this._fcf.create('BUILDING_FORM.BUILD_YEAR_LABEL', this.dataContext.buildYear),
      name: this._fcf.createMandatory('BUILDING_FORM.NAME_LABEL', this.dataContext.name),
      note: this._fcf.createMaxLength('COMMON.NOTE', this.dataContext.note, 3000),
      number: this._fcf.createMandatory('COMPANY.CODE', this.dataContext.number),
      floorsInBuilding: this._fcf.create('BUILDING_FORM.FLOORS_IN_BUILDING_LABEL', this.dataContext.floorsInBuilding),
      areaDescription: this._fcf.create(
        'BUILDING_FORM.AREA_DESCRIPTION',
        this.dataContext.areaDescription,
        null,
        null,
        'BUILDING_FORM.HELP_TEXT.AREA_DESCRIPTION'
      ),
      buildingDescription: this._fcf.create(
        'BUILDING_FORM.BUILDING_DESCRIPTION',
        this.dataContext.buildingDescription,
        null,
        null,
        'BUILDING_FORM.HELP_TEXT.BUILDING_DESCRIPTION'
      ),
      property: this._fcf.createMandatory('BUILDING_FORM.PROPERTY_LABEL', this.dataContext.property),
      reconstructionYear: this._fcf.create('BUILDING_FORM.RECONSTRUCTION_YEAR_LABEL', this.dataContext.reconstructionYear),
      entrances: this.fb.array(this.dataContext.entrances.map(item => this._createEntranceGroup(item))),
      areas: this.fb.array(this.dataContext.areas.map(item => this._createAreaGroup(item))),
      // WGS84 bounds. The API stores these in decimal(10,7) columns, so a value of 1000 or more -
      // SWEREF99 TM coordinates pasted into these fields, typically - used to fail the save with a
      // SQL arithmetic overflow and a 500 instead of pointing at the field.
      latitude: this._fcf.createWithOptions('COMMON.LATITUDE', this.dataContext.latitude, {
        minValue: -90,
        maxValue: 90,
      }),
      longitude: this._fcf.createWithOptions('COMMON.LONGITUDE', this.dataContext.longitude, {
        minValue: -180,
        maxValue: 180,
      }),
      centralLocation: this._fcf.create(
        'COMMON.CENTRAL_LOCATION',
        this.dataContext.centralLocation,
        null,
        null,
        'COMMON.HELP_TEXT.CENTRAL_LOCATION'
      ),
      dhMarketArea: this._fcf.createWithOptions(
        'BUILDING_FORM.DHMARKET_AREA',
        this.dataContext.dhMarketArea ? this.dataContext.dhMarketArea : '',
        {}
      ),
      costPlace: this._fcf.create(
        'PROPERTY.COST_PLACE',
        this.dataContext.costPlace,
        Validators.maxLength(10),
        null,
        this.getBuildingCostPlaceHelpText(this._dimensionLabelsService.defaults)
      ),
      costBearer: this._fcf.create(
        'PROPERTY.COST_BEARER',
        this.dataContext.costBearer,
        Validators.maxLength(10),
        null,
        this.getBuildingCostBearerHelpText(this._dimensionLabelsService.defaults)
      ),
      lettingShowingOrdinary: this._fcf.create('BUILDING_FORM.DHMARKET_SHOWING_ORDINARY', this.dataContext.lettingShowingOrdinary),
      buildingCustomProperties: this._createCustomPropertiesForm(this.dataContext.buildingCustomProperties),
    });

    this.buildingSpaces = this.fb.array([]);
    if (this.dataContext.buildingSpaces) {
      for (let space of this.dataContext.buildingSpaces) {
        this.buildingSpaces.push(this._createBuildingSpaceGroup(space));
      }
    }

    return group;
  }

  private _createCustomPropertiesForm(buildingCustomProperties: LinkedCustomProperty[]): FormArray {
    const formPropertiesArray = this.fb.array<FormGroup>([]);
    this.customProperties = [];
    for (let property of buildingCustomProperties ?? []) {
      const form = this.fb.group({
        id: this._fcf.createHidden(property.id),
        customPropertyId: this._fcf.createHidden(property.customPropertyId),
        numericValue: this._fcf.create(property.caption, property.numericValue),
        textValue: this._fcf.create(property.caption, property.textValue),
        dateValue: this._fcf.create(property.caption, property.dateValue),
        boolValue: this._fcf.create(property.caption, property.boolValue),
        dataType: this._fcf.create(property.caption, property.dataType),
      });
      formPropertiesArray.push(form);

      this.customProperties.push({
        form: form,
        selection: (property.selectableValues ?? []).map(x => new SelectionItem(x, x)),
        showInt: property.dataType === CustomPropertyDataType.Numeric && property.decimalCount === 0,
        showText: property.dataType === CustomPropertyDataType.Text,
        showDate: property.dataType === CustomPropertyDataType.Date,
        showBoolean: property.dataType === CustomPropertyDataType.Boolean,
        showSelection: property.dataType === CustomPropertyDataType.Selection,
        showNumeric: property.dataType === CustomPropertyDataType.Numeric && property.decimalCount !== 0,
      });
    }

    return formPropertiesArray;
  }

  addEntrance() {
    let entrance = new Entrance();
    entrance.code = (this.entrances.length + 1).toString().padStart(2, '0');
    this.entrances.push(this._createEntranceGroup(entrance));
  }

  useDhMarketShowings$ = this.userAccountService.current.pipe(
    switchMap(settings =>
      settings.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)
        ? this.lettingAdvertismentService.getDhMarketOptions().pipe(map(options => options.isShowingEnabled))
        : of(false)
    ),
    catchError(() => of(false))
  );

  removeEntrance(index: number) {
    this.entrances.removeAt(index);
    this.form.markAsDirty();
  }

  private bsSub: Subscription;

  addBuildingSpace() {
    if (this.bsSub) this.bsSub.unsubscribe();
    this.bsSub = this._dialogService
      .open(CreateBuildingSpaceDialogComponent, this._injector, null, { building: this.dataContext })
      .subscribe(val => {
        if (val) {
          this.buildingSpaces.push(this._createBuildingSpaceGroup(val as BuildingSpace));
        }
      });
  }

  editBuildingSpace(index: number) {
    if (this.bsSub) this.bsSub.unsubscribe();
    let form = this.buildingSpaces.controls[index] as FormGroup;
    this.bsSub = this._dialogService
      .open(EditBuildingSpaceDialogComponent, this._injector, null, { id: form.value.id }, true)
      .subscribe(val => {
        if (val) {
          form.controls['caption'].setValue(val.caption);
          form.controls['area'].setValue(val.area);
        }
      });
  }

  removeBuildingSpace(index: number) {
    let form = this.buildingSpaces.controls[index];
    if (this.bsSub) this.bsSub.unsubscribe();
    this.bsSub = this.messageService
      .showDialog('DO_YOU_WANT_TO_DELETE_TEXT', { value: form.value.caption })
      .pipe(
        switchMap(dialogResult => {
          if (dialogResult) {
            return this._buildingSpaceService.delete(form.value.id).pipe(
              tap(() => {
                this.buildingSpaces.removeAt(index);
              })
            );
          }
          return of(null);
        })
      )
      .subscribe();
  }

  addArea() {
    this.areas.push(this._createAreaGroup(new Area()));
  }

  removeArea(index: number) {
    this.areas.removeAt(index);
    this.form.markAsDirty();
  }

  dwelling: ResidentialProperty = new ResidentialProperty({ category: RentalObjectCategory.Dwelling });
  dwellingExist: boolean;
  premises: ResidentialProperty = new ResidentialProperty({ category: RentalObjectCategory.Premises });
  premisesExist: boolean;

  get rentalObjectsExist(): boolean {
    return this.dwellingExist || this.premisesExist;
  }

  private loadPropertyStatistics(): void {
    // Fixes the issue that only one of the charts is updated.
    // TODO: check if there's any better fix.
    this.AddSubscription(
      forkJoin({
        dwellingStatistics: this.dwellingService.getBuildingStatistics(this.dataContext.id),
        premisesStatistics: this.premisesService.getBuildingStatistics(this.dataContext.id),
      }).subscribe({
        next: data => {
          console.log(data);
          var dwellingStatistics = data.dwellingStatistics;
          dwellingStatistics.category = RentalObjectCategory.Dwelling;
          this.dwelling = new ResidentialProperty(dwellingStatistics);
          this.dwellingExist = this.dwelling.totalRentalObjectCount > 0;

          var premisesStatistics = data.premisesStatistics;
          premisesStatistics.category = RentalObjectCategory.Premises;
          this.premises = new ResidentialProperty(premisesStatistics);
          this.premisesExist = this.premises.totalRentalObjectCount > 0;
        },
        error: error => this.messageService.showError(error),
      })
    );
  }

  protected getHelpTextDictionary() {
    return this._helpTextService.getDictionary();
  }

  lettingOrdinaryChanged(event: AutoCompleteSelectionItem) {
    this.selectedOrdinary = event?.data;
  }

  private applyDimensionLabels(labels: DimensionLabels) {
    this._dimensionLabelsService.setControlCaption(this.form?.controls['costPlace'], labels.costPlaceLabel);
    this._dimensionLabelsService.setControlCaption(this.form?.controls['costBearer'], labels.costBearerLabel);
    this._dimensionLabelsService.setControlHelpText(this.form?.controls['costPlace'], this.getBuildingCostPlaceHelpText(labels));
    this._dimensionLabelsService.setControlHelpText(this.form?.controls['costBearer'], this.getBuildingCostBearerHelpText(labels));
  }

  private getBuildingCostPlaceHelpText(labels: DimensionLabels): string {
    return this._languageService.getText('BUILDING.HELP_TEXT.COST_PLACE', { dimension: labels.costPlaceLabel });
  }

  private getBuildingCostBearerHelpText(labels: DimensionLabels): string {
    return this._languageService.getText('BUILDING.HELP_TEXT.COST_BEARER', { dimension: labels.costBearerLabel });
  }
}
